/**
 * AgentJobsActions Component
 *
 * Simplified bulk action toolbar for agent Jobs tab showing:
 * - Rerun Jobs only (no Cancel - agent context is read-only monitoring)
 *
 * Follows the pattern from main Jobs but simplified for agent drawer context.
 */

import { ArrowPathIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import {
  type ActionConfigUnion,
  TableActionsBar,
} from '@/components/table/actions';
import { useReRunJob } from '@/hooks/useJobs';
import { Job, JobStatus } from '@/types';
import { cn } from '@/utils/classname';

export interface AgentJobsActionsProps {
  /** Jobs to perform actions on */
  jobs: Job[];

  /** Number of selected rows */
  selectedRowsCount: number;

  /** Callback to clear selection after successful action */
  onClearSelection: () => void;

  /** Disable all actions */
  disabled?: boolean;

  /** Additional CSS classes */
  className?: string;
}

/**
 * Simplified jobs bulk action component for agent drawer
 *
 * Provides Rerun operation only for selected jobs.
 * Integrates with TableActionsBar for consistent platform styling.
 */
export function AgentJobsActions({
  jobs,
  selectedRowsCount: _selectedRowsCount,
  onClearSelection,
  disabled = false,
  className,
}: AgentJobsActionsProps) {
  const { mutateAsync: reRunJob } = useReRunJob();

  // Filter jobs eligible for rerun (terminal states: Pass/Fail only)
  // Running/Queued jobs cannot be rerun (they're in progress)
  const eligibleForRerun = jobs.filter(job => {
    const status = job.status;
    return status === JobStatus.Pass || status === JobStatus.Fail;
  });

  const handleRerun = async () => {
    if (eligibleForRerun.length === 0) return;

    try {
      // Rerun all eligible jobs sequentially
      for (const job of eligibleForRerun) {
        await reRunJob({ jobKey: job.key });
      }
      // Note: Individual job mutations show their own toasts via useReRunJob
      // No additional toast here to avoid double-toast issue
      onClearSelection();
    } catch (error) {
      // Only show error toast if the mutation itself didn't show one
      const message =
        error instanceof Error ? error.message : 'Failed to rerun jobs';
      toast.error(message);
      console.error('Failed to rerun jobs:', error);
    }
  };

  const actions: ActionConfigUnion[] = [
    {
      type: 'Change Status' as const,
      render: () => (
        <Button
          data-testid="agent-jobs-rerun-button"
          onClick={handleRerun}
          disabled={disabled || eligibleForRerun.length === 0}
          styleType="rounded"
          size="xs"
          startIcon={<ArrowPathIcon className="size-4" />}
          aria-label={`Rerun ${eligibleForRerun.length} job${eligibleForRerun.length !== 1 ? 's' : ''}`}
        >
          Rerun {eligibleForRerun.length > 0 && `(${eligibleForRerun.length})`}
        </Button>
      ),
    },
  ];

  return (
    <TableActionsBar
      selectedItems={jobs}
      actions={actions}
      disabled={disabled}
      className={cn('py-2', className)}
      hideLabel={false}
      hideActions={false}
      testIdPrefix="agent-jobs"
    />
  );
}
